import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  theme,
  useContainerSpacing,
  useResponsive,
  VStack,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { getAppVersion } from '@order/utils';

import { useKustomerAutoStart } from '../../../integrations/kustomer';
import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';
import { resetStacksPostLogout } from '../../../screens/AccountScreen/AccountScreen.navigation';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import {
  AccountCustomerName,
  AccountMenuCrashOptions,
  AccountMenuSignOutItem,
  AccountPrimaryMenu,
  AccountSecondaryMenu,
} from './components';

export const AccountMenu = (props: AccountMenuProps) => {
  const { screen: screenId } = props;
  const { currentBreakpoint, match } = useResponsive();
  const { isMobile } = currentBreakpoint;
  const { isSigningOut, signOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const isCrashMenuEnabled = useFeatureFlag(
    'permanent-order-crash-option-enabled',
  );

  // ─── Callbacks ───────────────────────────────────────────────────────

  const signOutAndReset = React.useCallback(async () => {
    await signOut();
    resetStacksPostLogout();
  }, [signOut]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useKustomerAutoStart();

  // ─── Menu Styles ─────────────────────────────────────────────────────

  const dividerColor = isMobile ? theme.colors.DARK_KALE : theme.colors.LIGHT;
  const menuHorizontalSpacing = useContainerSpacing();
  const menuDividerSpacing = match([24, 32]);
  const menuContainerStyle = [
    useStyle(
      () => ({
        paddingVertical: match([40, 48]),
        paddingHorizontal: isMobile ? 0 : menuHorizontalSpacing,
      }),
      [isMobile, menuHorizontalSpacing],
    ),
    !isMobile ? menuContainerWebStyle : null,
  ];

  // ─── Name Styles ─────────────────────────────────────────────────────

  const nameSpacing = match([20, 16]);
  const nameContainerStyle = useStyle(
    () => ({
      paddingBottom: nameSpacing,
    }),
    [nameSpacing],
  );

  // ─── Logged In ───────────────────────────────────────────────────────

  if (isLoggedIn) {
    return (
      <VStack
        testID="account-menu"
        style={menuContainerStyle}
        customSpacing={menuDividerSpacing}
        divider
        dividerColor={dividerColor}
      >
        <View style={nameContainerStyle}>
          <AccountCustomerName />
        </View>

        <AccountPrimaryMenu
          screenId={screenId}
          customSpacing={match([theme.spacing.medium, 0])}
          divider={isMobile}
          dividerColor={theme.colors.LIGHT}
        />

        {Platform.OS !== 'web' ? (
          <VStack customSpacing={0}>
            <AccountSecondaryMenu />
          </VStack>
        ) : null}

        <VStack customSpacing={0}>
          <AccountMenuSignOutItem
            isSigningOut={isSigningOut}
            onSignOut={signOutAndReset}
          />
        </VStack>

        <VStack customSpacing={0}>
          {isCrashMenuEnabled ? <AccountMenuCrashOptions /> : null}
          <BodyText style={styles.appVersionText}>v{getAppVersion()}</BodyText>
        </VStack>
      </VStack>
    );
  }

  // ─── Logged Out ──────────────────────────────────────────────────────

  return (
    <VStack
      testID="account-menu"
      style={menuContainerStyle}
      customSpacing={menuDividerSpacing}
      divider
      dividerColor={dividerColor}
    >
      <AccountSignedOutMessage />

      {Platform.OS !== 'web' ? (
        <VStack customSpacing={0}>
          <AccountSecondaryMenu />
        </VStack>
      ) : null}

      <VStack customSpacing={0}>
        {isCrashMenuEnabled ? <AccountMenuCrashOptions /> : null}
        <BodyText style={styles.appVersionText}>v{getAppVersion()}</BodyText>
      </VStack>
    </VStack>
  );
};

type AccountMenuProps = {
  readonly screen?: AccountTabScreenId;
};

const styles = StyleSheet.create({
  appVersionText: {
    marginTop: theme.spacing.large,
  },
});

const menuContainerWebStyle = webOnlyStyles({
  position: 'sticky',
  top: 0,
} as unknown as Parameters<typeof webOnlyStyles>[0]);
